import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Calculator, History, Scissors, Sprout, Leaf, RotateCcw, FileText, Loader2, ExternalLink, Search, Lock, Plus, Trash2, Save, FolderOpen, Copy, Camera, Percent, ListPlus, Mail } from 'lucide-react';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import { Slider } from '../ui/slider';
import { Textarea } from '../ui/textarea';
import { Checkbox } from '../ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { RadioGroup, RadioGroupItem } from '../ui/radio-group';
import { Skeleton } from '../ui/skeleton';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { toast } from 'sonner';
import { easerGet, easerPost, easerPut, easerDelete, errorMessage, isUnauthorized, fmtShortDate, fmtTime } from '../../lib/easerApi';
import { DevisResult } from './devis/DevisResult';
import { DevisClientDialog, quotePdfUrl } from './devis/DevisClientDialog';
import { DevisSendDialog } from './devis/DevisSendDialog';
import { DevisPhotos, photoUrl } from './devis/DevisPhotos';
import { EMPTY_INPUTS, toPayload, fromPayload, hasContent, eur, STATUS_STYLE, newHaie, newTonte, newCustomLine, num } from './devis/devisFormat';

const SUBTABS = [
  { id: 'calcul', label: 'Calculatrice', icon: Calculator },
  { id: 'brouillons', label: 'Brouillons', icon: FolderOpen },
  { id: 'historique', label: 'Devis', icon: History },
];

const DRAFT_KEY = 'easer_devis_green_draft';

/** Onglet « Devis Green » (managers) : calculatrice d'aide au devis + génération du devis Dolibarr + brouillons + historique. */
export function EaserDevisGreen({ user, onUnauthorized }) {
  const [sub, setSub] = useState('calcul');
  const [grid, setGrid] = useState(null);
  const [gridError, setGridError] = useState(null);
  const [services, setServices] = useState([]);
  const [loadRequest, setLoadRequest] = useState(null); // {form, draft, photos} à charger dans la calculatrice
  const [draftCount, setDraftCount] = useState(null);
  const [sendQuote, setSendQuote] = useState(null); // devis à envoyer par e-mail
  const [historyKey, setHistoryKey] = useState(0);

  useEffect(() => {
    let alive = true;
    easerGet('/api/easer/devis-green/grid')
      .then((r) => { if (alive) setGrid(r.data); })
      .catch((err) => {
        if (isUnauthorized(err)) return onUnauthorized();
        if (alive) setGridError(err?.response?.status === 403 ? 'forbidden' : errorMessage(err, 'Grille de calcul indisponible'));
      });
    easerGet('/api/easer/devis-green/services').then((r) => { if (alive) setServices(r.data.items || []); }).catch(() => {});
    easerGet('/api/easer/devis-green/drafts').then((r) => { if (alive) setDraftCount((r.data.items || []).length); }).catch(() => {});
    return () => { alive = false; };
  }, [onUnauthorized]);

  const openInCalculator = useCallback((payload, meta = {}) => {
    setLoadRequest({ form: fromPayload(payload), draft: meta.draft || null, photos: meta.photos || [], at: Date.now() });
    setSub('calcul');
  }, []);

  if (gridError === 'forbidden') {
    return (
      <Card className="p-5 text-center" data-testid="devis-forbidden">
        <Lock className="h-8 w-8 mx-auto text-muted-foreground mb-2" />
        <p className="font-semibold">Accès réservé aux managers</p>
        <p className="text-sm text-muted-foreground">La calculatrice de devis est ouverte aux utilisateurs portant le tag « MANAGER » dans Dolibarr.</p>
      </Card>
    );
  }

  return (
    <div className="space-y-4" data-testid="easer-devis-green">
      <div className="grid grid-cols-3 gap-1 rounded-xl border border-border bg-card p-1" role="tablist" aria-label="Devis Green">
        {SUBTABS.map((t) => {
          const active = sub === t.id;
          return (
            <button key={t.id} type="button" role="tab" aria-selected={active} onClick={() => setSub(t.id)} data-testid={`devis-sub-${t.id}`}
              className={`flex items-center justify-center gap-2 min-h-[44px] rounded-lg px-2 text-sm font-semibold transition-colors ${active ? 'bg-[hsl(var(--primary))] text-white' : 'text-muted-foreground hover:text-foreground hover:bg-secondary'}`}>
              <t.icon className="h-4 w-4" /> {t.label}{t.id === 'brouillons' && draftCount > 0 && <span className={`text-[11px] rounded-full px-1.5 ${active ? 'bg-white/20' : 'bg-secondary'}`} data-testid="devis-draft-count">{draftCount}</span>}
            </button>
          );
        })}
      </div>
      {sub === 'calcul' && (gridError ? <p className="text-sm text-destructive" role="alert">{gridError}</p> : grid ? (
        <Calculatrice grid={grid} services={services} user={user} onUnauthorized={onUnauthorized} loadRequest={loadRequest} onSend={setSendQuote}
          onCreated={() => setSub('historique')} onDraftSaved={() => easerGet('/api/easer/devis-green/drafts').then((r) => setDraftCount((r.data.items || []).length)).catch(() => {})} />
      ) : <Skeleton className="h-64 w-full" />)}
      {sub === 'brouillons' && <Brouillons onUnauthorized={onUnauthorized} onOpen={openInCalculator} onCountChange={setDraftCount} />}
      {sub === 'historique' && <Historique onUnauthorized={onUnauthorized} onDuplicate={(p) => openInCalculator(p)} onSend={setSendQuote} refreshKey={historyKey} />}
      <DevisSendDialog quote={sendQuote} onClose={() => setSendQuote(null)} onUnauthorized={onUnauthorized} onSent={() => { setHistoryKey((k) => k + 1); setSub('historique'); }} />
    </div>
  );
}

// ---------------------------------------------------------------- calculatrice

function Calculatrice({ grid, services, onUnauthorized, onCreated, loadRequest, onDraftSaved, onSend }) {
  const [form, setForm] = useState(() => {
    try {
      const saved = JSON.parse(sessionStorage.getItem(DRAFT_KEY) || 'null');
      return saved?.form ? { ...EMPTY_INPUTS, ...saved.form } : EMPTY_INPUTS;
    } catch { return EMPTY_INPUTS; }
  });
  const [draft, setDraft] = useState(() => { try { return JSON.parse(sessionStorage.getItem(DRAFT_KEY) || 'null')?.draft || null; } catch { return null; } });
  const [photos, setPhotos] = useState(() => { try { return JSON.parse(sessionStorage.getItem(DRAFT_KEY) || 'null')?.photos || []; } catch { return []; } });
  const [result, setResult] = useState(null);
  const [computing, setComputing] = useState(false);
  const [error, setError] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [draftDialog, setDraftDialog] = useState(false);
  const seq = useRef(0);
  const lastLoad = useRef(null);

  useEffect(() => {
    if (loadRequest && loadRequest.at !== lastLoad.current) {
      lastLoad.current = loadRequest.at;
      setForm(loadRequest.form);
      setDraft(loadRequest.draft);
      setPhotos(loadRequest.photos || []);
      toast.info(loadRequest.draft ? `Brouillon « ${loadRequest.draft.title} » ouvert` : 'Estimation dupliquée : ajustez puis générez le devis');
    }
  }, [loadRequest]);

  const payload = useMemo(() => toPayload(form), [form]);
  const filled = hasContent(payload);

  useEffect(() => {
    try { sessionStorage.setItem(DRAFT_KEY, JSON.stringify({ form, draft, photos })); } catch { /* quota */ }
  }, [form, draft, photos]);

  useEffect(() => {
    if (!filled) { setResult(null); setError(null); return undefined; }
    const mine = ++seq.current;
    setComputing(true);
    const timer = setTimeout(async () => {
      try {
        const r = await easerPost('/api/easer/devis-green/compute', payload);
        if (mine === seq.current) { setResult(r.data); setError(null); }
      } catch (err) {
        if (isUnauthorized(err)) return onUnauthorized();
        if (mine === seq.current) setError(errorMessage(err, 'Calcul impossible'));
      } finally {
        if (mine === seq.current) setComputing(false);
      }
    }, 400);
    return () => clearTimeout(timer);
  }, [payload, filled, onUnauthorized]);

  const set = (patch) => setForm((f) => ({ ...f, ...patch }));
  const setList = (key, idx, patch) => setForm((f) => ({ ...f, [key]: f[key].map((it, i) => (i === idx ? { ...it, ...patch } : it)) }));
  const removeFrom = (key, idx) => setForm((f) => ({ ...f, [key]: f[key].filter((_, i) => i !== idx), adjustments: {} }));
  const setExtra = (id, patch) => setForm((f) => ({ ...f, extras: { ...f.extras, [id]: { qty: '', modifiers: [], ...(f.extras[id] || {}), ...patch } } }));
  const adjust = (blockId, delta) => setForm((f) => {
    const next = { ...(f.adjustments || {}) };
    if (delta === null) delete next[blockId];
    else next[blockId] = Math.round(((Number(next[blockId]) || 0) + delta) * 100) / 100;
    return { ...f, adjustments: next };
  });
  const reset = () => { setForm(EMPTY_INPUTS); setResult(null); setDraft(null); setPhotos([]); };

  const regular = form.rhythm === 'regulier';
  const extras = (grid.extras || []).filter((p) => p.enabled !== false);
  const hourly = regular ? grid.service_details?.hourly_regulier : grid.service_details?.hourly_ponctuel;

  const saveDraft = async (title) => {
    const body = { title, inputs: payload, client_name: null };
    try {
      let r;
      if (draft?.id) r = await easerPut(`/api/easer/devis-green/drafts/${draft.id}`, body);
      else r = await easerPost('/api/easer/devis-green/drafts', body);
      setDraft(r.data);
      // lie les photos déjà envoyées au brouillon (pour les retrouver à la réouverture)
      await Promise.all(photos.filter((p) => p.draft_id !== r.data.id).map((p) => easerPut(`/api/easer/devis-green/photos/${p.id}`, { caption: p.caption || '', draft_id: r.data.id }).catch(() => {})));
      setPhotos((ps) => ps.map((p) => ({ ...p, draft_id: r.data.id })));
      toast.success(`Brouillon « ${r.data.title} » enregistré`);
      onDraftSaved?.();
      setDraftDialog(false);
    } catch (err) {
      if (isUnauthorized(err)) return onUnauthorized();
      toast.error(errorMessage(err, 'Enregistrement du brouillon impossible'));
    }
  };

  return (
    <div className="space-y-4">
      {draft && (
        <p className="text-xs text-muted-foreground flex items-center gap-2 px-1" data-testid="devis-current-draft"><FolderOpen className="h-3.5 w-3.5" /> Brouillon : <span className="font-medium text-foreground">{draft.title}</span></p>
      )}
      <Card className="p-4 space-y-3" data-testid="devis-rhythm-card">
        <div className="flex items-center justify-between gap-3">
          <div>
            <p className="font-semibold flex items-center gap-2"><Leaf className="h-4 w-4 text-[hsl(var(--primary))]" /> Rythme</p>
            <p className="text-xs text-muted-foreground">Tarif horaire {hourly?.ref} : {eur(hourly?.price_ttc)} TTC</p>
          </div>
          <Button type="button" variant="ghost" size="sm" onClick={reset} className="text-muted-foreground" data-testid="devis-reset"><RotateCcw className="h-4 w-4 mr-1" /> Vider</Button>
        </div>
        <RadioGroup value={form.rhythm} onValueChange={(v) => set({ rhythm: v })} className="grid grid-cols-2 gap-2">
          <label className={`flex items-center gap-2 rounded-lg border p-2.5 text-sm cursor-pointer ${!regular ? 'border-[hsl(var(--primary))] bg-[hsl(var(--primary))]/5' : 'border-border'}`}><RadioGroupItem value="ponctuel" data-testid="devis-rhythm-ponctuel" /> Ponctuel</label>
          <label className={`flex items-center gap-2 rounded-lg border p-2.5 text-sm cursor-pointer ${regular ? 'border-[hsl(var(--primary))] bg-[hsl(var(--primary))]/5' : 'border-border'}`}><RadioGroupItem value="regulier" data-testid="devis-rhythm-regulier" /> Régulier</label>
        </RadioGroup>
        {regular && (
          <div className="flex items-center gap-3">
            <Label htmlFor="devis-visits" className="whitespace-nowrap">Passages dans le contrat</Label>
            <Input id="devis-visits" type="number" inputMode="numeric" min={1} max={60} value={form.visits} onChange={(e) => set({ visits: e.target.value })} className="h-11 w-24" data-testid="devis-visits" />
          </div>
        )}
      </Card>

      {grid.haie?.enabled !== false && (
        <ListSection icon={Scissors} title={grid.haie.label} items={form.haies} testId="devis-haie" addLabel="Ajouter une haie"
          onAdd={() => set({ haies: [...form.haies, newHaie(form.haies.length ? `Haie ${form.haies.length + 1}` : '')] })}>
          {form.haies.map((h, i) => (
            <ListItem key={h.key} index={i} name={h.name} onName={(v) => setList('haies', i, { name: v })} onRemove={() => removeFrom('haies', i)} placeholder="Haie avant, haie du fond…" testId={`devis-haie-${i}`}>
              <div className="grid grid-cols-3 gap-2">
                <NumField id={`haie-${i}-length`} label="Longueur (m)" value={h.length} onChange={(v) => setList('haies', i, { length: v })} testId={`devis-haie-${i}-length`} />
                <NumField id={`haie-${i}-height`} label="Hauteur (m)" value={h.height} onChange={(v) => setList('haies', i, { height: v })} step="0.1" testId={`devis-haie-${i}-height`} />
                <NumField id={`haie-${i}-width`} label="Largeur (m)" value={h.width} onChange={(v) => setList('haies', i, { width: v })} step="0.1" testId={`devis-haie-${i}-width`} />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label className="text-xs">Faces à tailler</Label>
                  <Select value={h.faces} onValueChange={(v) => setList('haies', i, { faces: v })}>
                    <SelectTrigger className="h-11" data-testid={`devis-haie-${i}-faces`}><SelectValue /></SelectTrigger>
                    <SelectContent>{grid.haie.faces.map((f) => <SelectItem key={f.id} value={f.id}>{f.label}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <NumField id={`haie-${i}-reduction`} label="Réduction (m, 0 = taille simple)" value={h.reduction} onChange={(v) => setList('haies', i, { reduction: v })} step="0.1" testId={`devis-haie-${i}-reduction`} />
              </div>
              <label className="flex items-center justify-between gap-3 text-sm">
                <span>Enlèvement des déchets (remorque)</span>
                <Switch checked={!!h.evacuate} onCheckedChange={(v) => setList('haies', i, { evacuate: v })} data-testid={`devis-haie-${i}-evacuate`} />
              </label>
            </ListItem>
          ))}
        </ListSection>
      )}

      {grid.tonte?.enabled !== false && (
        <ListSection icon={Sprout} title={grid.tonte.label} items={form.tontes} testId="devis-tonte" addLabel="Ajouter une pelouse"
          onAdd={() => set({ tontes: [...form.tontes, newTonte(form.tontes.length ? `Pelouse ${form.tontes.length + 1}` : '')] })}>
          {form.tontes.map((t, i) => (
            <ListItem key={t.key} index={i} name={t.name} onName={(v) => setList('tontes', i, { name: v })} onRemove={() => removeFrom('tontes', i)} placeholder="Pelouse avant, arrière…" testId={`devis-tonte-${i}`}>
              <div className="grid grid-cols-3 gap-2">
                <NumField id={`tonte-${i}-surface`} label="Surface (m²)" value={t.surface} onChange={(v) => setList('tontes', i, { surface: v })} testId={`devis-tonte-${i}-surface`} />
                <NumField id={`tonte-${i}-grass`} label="Herbe actuelle (cm)" value={t.grass_height} onChange={(v) => setList('tontes', i, { grass_height: v })} testId={`devis-tonte-${i}-grass`} />
                <NumField id={`tonte-${i}-target`} label="Hauteur souhaitée (cm)" value={t.target_height} onChange={(v) => setList('tontes', i, { target_height: v })} testId={`devis-tonte-${i}-target`} />
              </div>
              <div>
                <Label className="text-xs">Nature du terrain</Label>
                <Select value={t.terrain} onValueChange={(v) => setList('tontes', i, { terrain: v })}>
                  <SelectTrigger className="h-11" data-testid={`devis-tonte-${i}-terrain`}><SelectValue /></SelectTrigger>
                  <SelectContent>{grid.tonte.terrain.map((o) => <SelectItem key={o.id} value={o.id}>{o.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <SliderField label="Encombrement de la pelouse" value={t.clutter} onChange={(v) => setList('tontes', i, { clutter: v })} testId={`devis-tonte-${i}-clutter`} />
              <SliderField label="Niveau technique" value={t.technical} onChange={(v) => setList('tontes', i, { technical: v })} testId={`devis-tonte-${i}-technical`} />
              <div>
                <Label className="text-xs mb-1.5 block">Déchets de tonte</Label>
                <RadioGroup value={t.waste} onValueChange={(v) => setList('tontes', i, { waste: v })} className="grid grid-cols-3 gap-2">
                  {[['none', 'Laissés sur place'], ['bags', 'Mise en sac'], ['pickup', 'Enlèvement']].map(([v, l]) => (
                    <label key={v} className={`flex items-center gap-2 rounded-lg border p-2 text-xs cursor-pointer ${t.waste === v ? 'border-[hsl(var(--primary))] bg-[hsl(var(--primary))]/5' : 'border-border'}`}><RadioGroupItem value={v} data-testid={`devis-tonte-${i}-waste-${v}`} /> {l}</label>
                  ))}
                </RadioGroup>
              </div>
            </ListItem>
          ))}
        </ListSection>
      )}

      {extras.length > 0 && (
        <Card className="p-4 space-y-3" data-testid="devis-extras">
          <p className="font-semibold flex items-center gap-2"><Leaf className="h-4 w-4 text-[hsl(var(--primary))]" /> Autres prestations</p>
          {extras.map((p) => {
            const v = form.extras[p.id] || { qty: '', modifiers: [] };
            return (
              <div key={p.id} className="space-y-1.5" data-testid={`devis-extra-${p.id}`}>
                <div className="flex items-end gap-2">
                  <div className="flex-1 min-w-0">
                    <Label htmlFor={`extra-${p.id}`} className="text-sm">{p.label}</Label>
                    {p.help && <p className="text-[11px] text-muted-foreground truncate">{p.help}</p>}
                  </div>
                  <div className="relative w-28">
                    <Input id={`extra-${p.id}`} type="number" inputMode="decimal" min={0} value={v.qty} onChange={(e) => setExtra(p.id, { qty: e.target.value })} className="h-11 pr-10" placeholder="0" data-testid={`devis-extra-${p.id}-qty`} />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-muted-foreground">{p.unit}</span>
                  </div>
                </div>
                {p.modifiers?.length > 0 && num(v.qty) > 0 && (
                  <div className="flex flex-wrap gap-3 pl-1">
                    {p.modifiers.map((m) => (
                      <label key={m.id} className="flex items-center gap-1.5 text-xs">
                        <Checkbox checked={v.modifiers.includes(m.id)} onCheckedChange={(c) => setExtra(p.id, { modifiers: c ? [...v.modifiers, m.id] : v.modifiers.filter((x) => x !== m.id) })} data-testid={`devis-extra-${p.id}-mod-${m.id}`} /> {m.label}
                      </label>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </Card>
      )}

      <Card className="p-4 space-y-3" data-testid="devis-custom-lines">
        <div className="flex items-center justify-between gap-3">
          <p className="font-semibold flex items-center gap-2"><ListPlus className="h-4 w-4 text-[hsl(var(--primary))]" /> Lignes libres et remise</p>
          <Button type="button" variant="outline" size="sm" className="h-9" onClick={() => set({ custom_lines: [...form.custom_lines, newCustomLine()] })} data-testid="devis-custom-add"><Plus className="h-4 w-4 mr-1" /> Ligne</Button>
        </div>
        {form.custom_lines.map((l, i) => (
          <div key={l.key} className="rounded-lg border border-border p-3 space-y-2" data-testid={`devis-custom-${i}`}>
            <div className="flex items-center justify-between gap-2">
              <RadioGroup value={l.mode} onValueChange={(v) => setList('custom_lines', i, { mode: v })} className="flex gap-3">
                <label className="flex items-center gap-1.5 text-xs"><RadioGroupItem value="free" data-testid={`devis-custom-${i}-mode-free`} /> Prix libre</label>
                <label className="flex items-center gap-1.5 text-xs"><RadioGroupItem value="service" data-testid={`devis-custom-${i}-mode-service`} /> Service Dolibarr</label>
              </RadioGroup>
              <Button type="button" variant="ghost" size="icon" className="h-8 w-8 text-destructive" onClick={() => removeFrom('custom_lines', i)} aria-label="Supprimer la ligne" data-testid={`devis-custom-${i}-remove`}><Trash2 className="h-4 w-4" /></Button>
            </div>
            {l.mode === 'service' ? (
              <Select value={l.product_id} onValueChange={(v) => { const s = services.find((x) => String(x.id) === v); setList('custom_lines', i, { product_id: v, sap_eligible: s ? !!s.sap_eligible : false }); }}>
                <SelectTrigger className="h-11" data-testid={`devis-custom-${i}-service`}><SelectValue placeholder="Choisir un service" /></SelectTrigger>
                <SelectContent className="max-h-72">{services.map((s) => <SelectItem key={s.id} value={String(s.id)}>{s.ref} — {s.label} ({eur(s.price_ttc)} TTC){s.sap_eligible ? ' · SAP' : ''}</SelectItem>)}</SelectContent>
              </Select>
            ) : (
              <Input value={l.label} onChange={(e) => setList('custom_lines', i, { label: e.target.value })} placeholder="Libellé (ex. fournitures, terreau…)" className="h-11" data-testid={`devis-custom-${i}-label`} />
            )}
            <div className="grid grid-cols-3 gap-2">
              <NumField id={`custom-${i}-qty`} label="Quantité" value={l.qty} onChange={(v) => setList('custom_lines', i, { qty: v })} testId={`devis-custom-${i}-qty`} />
              {l.mode === 'free' && <NumField id={`custom-${i}-price`} label="Prix unitaire TTC (€)" value={l.unit_price_ttc} onChange={(v) => setList('custom_lines', i, { unit_price_ttc: v })} step="0.01" testId={`devis-custom-${i}-price`} />}
              {l.mode === 'free' && (
                <div>
                  <Label className="text-xs">TVA</Label>
                  <Select value={l.tva_tx} onValueChange={(v) => setList('custom_lines', i, { tva_tx: v })}>
                    <SelectTrigger className="h-11" data-testid={`devis-custom-${i}-tva`}><SelectValue /></SelectTrigger>
                    <SelectContent>{['20', '10', '5.5', '0'].map((r) => <SelectItem key={r} value={r}>{r.replace('.', ',')} %</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              )}
            </div>
            <label className={`flex items-center justify-between gap-2 rounded-md px-2.5 py-2 text-xs ${l.sap_eligible === false ? 'bg-[hsl(var(--brand-apricot))]/15 text-foreground' : 'bg-secondary/60 text-muted-foreground'}`} data-testid={`devis-custom-${i}-sap-row`}>
              <span className="flex items-center gap-1.5">
                <Percent className="h-3.5 w-3.5" />
                {l.sap_eligible === false ? 'Hors services à la personne : plein tarif, sans crédit d\'impôt' : 'Éligible services à la personne (avance immédiate crédit d\'impôt)'}
                {l.mode === 'service' && l.product_id && <span className="text-[10px] opacity-70">· classement agence</span>}
              </span>
              <Switch checked={l.sap_eligible !== false} onCheckedChange={(v) => setList('custom_lines', i, { sap_eligible: v })} aria-label="Éligible aux services à la personne" data-testid={`devis-custom-${i}-sap`} />
            </label>
          </div>
        ))}
        <div className="flex items-center gap-3">
          <Label htmlFor="devis-discount" className="flex items-center gap-1 whitespace-nowrap"><Percent className="h-3.5 w-3.5" /> Remise sur le devis</Label>
          <div className="relative w-28">
            <Input id="devis-discount" type="number" inputMode="decimal" min={0} max={100} step="0.5" value={form.discount_percent} onChange={(e) => set({ discount_percent: e.target.value })} className="h-11 pr-8" placeholder="0" data-testid="devis-discount" />
            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-muted-foreground">%</span>
          </div>
        </div>
      </Card>

      <DevisPhotos photos={photos} onChange={setPhotos} draftId={draft?.id} onUnauthorized={onUnauthorized} />

      <Card className="p-4 space-y-3">
        {grid.rules?.km_enabled && <NumField id="devis-km" label="Kilomètres aller-retour" value={form.km} onChange={(v) => set({ km: v })} testId="devis-km" />}
        <div>
          <Label htmlFor="devis-comment" className="text-xs">Note interne (visible dans Dolibarr uniquement)</Label>
          <Textarea id="devis-comment" value={form.comment} onChange={(e) => set({ comment: e.target.value })} rows={2} placeholder="Accès, contraintes, souhaits du client…" data-testid="devis-comment" />
        </div>
      </Card>

      <Card className="p-4 space-y-3" data-testid="devis-result-card">
        <p className="font-semibold flex items-center gap-2"><Calculator className="h-4 w-4 text-[hsl(var(--primary))]" /> Estimation {computing && <Loader2 className="h-3.5 w-3.5 animate-spin text-muted-foreground" />}</p>
        <DevisResult result={filled ? result : null} loading={computing} error={error} adjustments={form.adjustments} onAdjust={adjust} />
        <div className="grid grid-cols-[auto_1fr] gap-2">
          <Button type="button" variant="outline" disabled={!filled} onClick={() => setDraftDialog(true)} className="h-12" data-testid="devis-save-draft"><Save className="h-4 w-4 mr-1" /> {draft ? 'Enregistrer' : 'Brouillon'}</Button>
          <Button type="button" disabled={!result || !result.lines?.length || computing} onClick={() => setDialogOpen(true)} className="h-12 bg-[hsl(var(--primary))] hover:bg-[hsl(var(--primary))]/90 text-white" data-testid="devis-generate">
            <FileText className="h-4 w-4 mr-1" /> Générer le devis Dolibarr
          </Button>
        </div>
      </Card>

      <DevisClientDialog open={dialogOpen} onOpenChange={setDialogOpen} payload={payload} result={result} photoIds={photos.map((p) => p.id)} draftId={draft?.id} onUnauthorized={onUnauthorized} onSend={onSend}
        onCreated={() => { setForm(EMPTY_INPUTS); setResult(null); setDraft(null); setPhotos([]); onDraftSaved?.(); onCreated?.(); }} />
      <DraftTitleDialog open={draftDialog} onOpenChange={setDraftDialog} initial={draft?.title || ''} onSave={saveDraft} />
    </div>
  );
}

function DraftTitleDialog({ open, onOpenChange, initial, onSave }) {
  const [title, setTitle] = useState(initial);
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) setTitle(initial || ''); }, [open, initial]);
  const submit = async (e) => {
    e.preventDefault();
    if (!title.trim()) return;
    setBusy(true);
    try { await onSave(title.trim()); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-sm w-[95vw]" data-testid="devis-draft-dialog">
        <DialogHeader>
          <DialogTitle>Enregistrer le brouillon</DialogTitle>
          <DialogDescription>Retrouvez cette estimation plus tard dans l'onglet Brouillons (partagé entre managers).</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div>
            <Label htmlFor="draft-title">Nom du brouillon</Label>
            <Input id="draft-title" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="M. Dupont — Liévin" autoFocus className="h-11" maxLength={120} data-testid="devis-draft-title" />
          </div>
          <Button type="submit" disabled={busy || !title.trim()} className="w-full h-11 bg-[hsl(var(--primary))] hover:bg-[hsl(var(--primary))]/90 text-white" data-testid="devis-draft-save">{busy ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Save className="h-4 w-4 mr-1" />} Enregistrer</Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function ListSection({ icon: Icon, title, items, onAdd, addLabel, children, testId }) {
  return (
    <Card className="p-4 space-y-3" data-testid={testId}>
      <div className="flex items-center justify-between gap-3">
        <span className="font-semibold flex items-center gap-2"><Icon className="h-4 w-4 text-[hsl(var(--primary))]" /> {title}{items.length > 1 && <span className="text-xs font-normal text-muted-foreground">× {items.length}</span>}</span>
        <Button type="button" variant={items.length ? 'outline' : 'default'} size="sm" className={`h-9 ${items.length ? '' : 'bg-[hsl(var(--primary))] hover:bg-[hsl(var(--primary))]/90 text-white'}`} onClick={onAdd} data-testid={`${testId}-add`}><Plus className="h-4 w-4 mr-1" /> {items.length ? 'Ajouter' : addLabel}</Button>
      </div>
      {items.length > 0 && <div className="space-y-3">{children}</div>}
    </Card>
  );
}

function ListItem({ index, name, onName, onRemove, placeholder, children, testId }) {
  return (
    <div className="rounded-lg border border-border p-3 space-y-3" data-testid={testId}>
      <div className="flex items-center gap-2">
        <span className="h-6 w-6 rounded-full bg-[hsl(var(--primary))]/10 text-[hsl(var(--primary))] text-xs font-semibold inline-flex items-center justify-center flex-shrink-0">{index + 1}</span>
        <Input value={name || ''} onChange={(e) => onName(e.target.value)} placeholder={placeholder} className="h-9 text-sm" maxLength={60} data-testid={`${testId}-name`} />
        <Button type="button" variant="ghost" size="icon" className="h-9 w-9 text-destructive flex-shrink-0" onClick={onRemove} aria-label="Supprimer" data-testid={`${testId}-remove`}><Trash2 className="h-4 w-4" /></Button>
      </div>
      {children}
    </div>
  );
}

function NumField({ id, label, value, onChange, step = 'any', testId }) {
  return (
    <div>
      <Label htmlFor={id} className="text-xs">{label}</Label>
      <Input id={id} type="number" inputMode="decimal" min={0} step={step} value={value} onChange={(e) => onChange(e.target.value)} className="h-11" placeholder="0" data-testid={testId} />
    </div>
  );
}

function SliderField({ label, value, onChange, testId }) {
  const v = Math.min(10, Math.max(1, Number(value) || 1));
  return (
    <div>
      <div className="flex items-center justify-between text-xs mb-1.5"><Label>{label}</Label><span className="font-semibold" data-testid={`${testId}-value`}>{v}/10</span></div>
      <Slider value={[v]} min={1} max={10} step={1} onValueChange={([n]) => onChange(n)} data-testid={testId} aria-label={label} />
    </div>
  );
}

// ---------------------------------------------------------------- brouillons partagés

function Brouillons({ onUnauthorized, onOpen, onCountChange }) {
  const [items, setItems] = useState(null);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    try {
      const r = await easerGet('/api/easer/devis-green/drafts');
      setItems(r.data.items || []);
      onCountChange?.((r.data.items || []).length);
      setError(null);
    } catch (err) {
      if (isUnauthorized(err)) return onUnauthorized();
      setError(errorMessage(err, 'Brouillons indisponibles'));
      setItems((i) => i || []);
    }
  }, [onUnauthorized, onCountChange]);

  useEffect(() => { load(); }, [load]);

  const open = async (d) => {
    try {
      const r = await easerGet(`/api/easer/devis-green/drafts/${d.id}`);
      onOpen(r.data.inputs, { draft: { id: r.data.id, title: r.data.title }, photos: r.data.photos || [] });
    } catch (err) {
      if (isUnauthorized(err)) return onUnauthorized();
      toast.error(errorMessage(err, 'Ouverture impossible'));
    }
  };

  const remove = async (d) => {
    if (!window.confirm(`Supprimer le brouillon « ${d.title} » ?`)) return;
    try {
      await easerDelete(`/api/easer/devis-green/drafts/${d.id}`);
      toast.success('Brouillon supprimé');
      load();
    } catch (err) {
      if (isUnauthorized(err)) return onUnauthorized();
      toast.error(errorMessage(err, 'Suppression impossible'));
    }
  };

  return (
    <div className="space-y-3" data-testid="devis-drafts">
      {error && <p className="text-sm text-destructive flex items-center justify-between" role="alert">{error} <Button size="sm" variant="outline" onClick={load}>Réessayer</Button></p>}
      {items === null ? <Skeleton className="h-32 w-full" /> : items.length === 0 ? (
        <Card className="p-5 text-center text-sm text-muted-foreground" data-testid="devis-drafts-empty">Aucun brouillon. Depuis la calculatrice, « Brouillon » enregistre une estimation à reprendre plus tard.</Card>
      ) : (
        <ul className="space-y-2">
          {items.map((d) => (
            <li key={d.id}>
              <Card className="p-3" data-testid={`devis-draft-item-${d.id}`}>
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <p className="font-semibold truncate">{d.title}</p>
                    <p className="text-xs text-muted-foreground">{fmtShortDate(d.updated_at)} {fmtTime(d.updated_at)} · {d.created_by?.name || d.created_by?.login}{d.photo_count ? ` · ${d.photo_count} photo${d.photo_count > 1 ? 's' : ''}` : ''}</p>
                    <p className="text-xs text-muted-foreground">{summarizeInputs(d.inputs)}</p>
                  </div>
                  <Button type="button" variant="ghost" size="icon" className="h-9 w-9 text-destructive flex-shrink-0" onClick={() => remove(d)} aria-label="Supprimer" data-testid={`devis-draft-${d.id}-delete`}><Trash2 className="h-4 w-4" /></Button>
                </div>
                <Button type="button" size="sm" className="mt-2 h-9 bg-[hsl(var(--primary))] hover:bg-[hsl(var(--primary))]/90 text-white" onClick={() => open(d)} data-testid={`devis-draft-${d.id}-open`}><FolderOpen className="h-4 w-4 mr-1" /> Reprendre</Button>
              </Card>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

const summarizeInputs = (p) => {
  if (!p) return '';
  const bits = [];
  const haies = [...(p.haies || []), ...(p.haie?.length ? [p.haie] : [])];
  const tontes = [...(p.tontes || []), ...(p.tonte?.surface ? [p.tonte] : [])];
  if (haies.length) bits.push(`${haies.length} haie${haies.length > 1 ? 's' : ''} (${haies.map((h) => `${h.length} m`).join(', ')})`);
  if (tontes.length) bits.push(`${tontes.length} pelouse${tontes.length > 1 ? 's' : ''} (${tontes.map((t) => `${t.surface} m²`).join(', ')})`);
  const extras = Object.keys(p.extras || {}).length;
  if (extras) bits.push(`${extras} autre${extras > 1 ? 's' : ''} prestation${extras > 1 ? 's' : ''}`);
  if (p.custom_lines?.length) bits.push(`${p.custom_lines.length} ligne${p.custom_lines.length > 1 ? 's' : ''} libre${p.custom_lines.length > 1 ? 's' : ''}`);
  bits.push(p.rhythm === 'regulier' ? `régulier × ${p.visits || 1}` : 'ponctuel');
  return bits.join(' · ');
};

// ---------------------------------------------------------------- historique partagé

function Historique({ onUnauthorized, onDuplicate, onSend, refreshKey = 0 }) {
  const [items, setItems] = useState(null);
  const [q, setQ] = useState('');
  const [error, setError] = useState(null);
  const [photosFor, setPhotosFor] = useState(null); // {quote, items}

  const load = useCallback(async () => {
    try {
      const r = await easerGet('/api/easer/devis-green/quotes', q.trim() ? { q: q.trim() } : {});
      setItems(r.data.items || []);
      setError(null);
    } catch (err) {
      if (isUnauthorized(err)) return onUnauthorized();
      setError(errorMessage(err, 'Historique indisponible'));
      setItems((i) => i || []);
    }
  }, [q, onUnauthorized]);

  useEffect(() => { const t = setTimeout(load, q ? 350 : 0); return () => clearTimeout(t); }, [load, q, refreshKey]);

  const showPhotos = async (d) => {
    try {
      const r = await easerGet(`/api/easer/devis-green/quotes/${d.id}/photos`);
      setPhotosFor({ quote: d, items: r.data.items || [] });
    } catch (err) {
      if (isUnauthorized(err)) return onUnauthorized();
      toast.error(errorMessage(err, 'Photos indisponibles'));
    }
  };

  return (
    <div className="space-y-3" data-testid="devis-history">
      <div className="relative">
        <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Client, référence, manager…" className="pl-9 h-11" data-testid="devis-history-search" />
      </div>
      {error && <p className="text-sm text-destructive flex items-center justify-between" role="alert">{error} <Button size="sm" variant="outline" onClick={load}>Réessayer</Button></p>}
      {items === null ? <Skeleton className="h-32 w-full" /> : items.length === 0 ? (
        <Card className="p-5 text-center text-sm text-muted-foreground" data-testid="devis-history-empty">Aucun devis généré depuis l'application pour le moment.</Card>
      ) : (
        <ul className="space-y-2">
          {items.map((d) => (
            <li key={d.id}>
              <Card className="p-3" data-testid={`devis-history-item-${d.id}`}>
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <p className="font-semibold truncate">{d.client?.name}</p>
                    <p className="text-xs text-muted-foreground">{d.dolibarr?.ref} · {fmtShortDate(d.created_at)} {fmtTime(d.created_at)} · {d.created_by?.name || d.created_by?.login}</p>
                  </div>
                  <span className={`text-[11px] font-semibold rounded-full border px-2 py-0.5 whitespace-nowrap ${STATUS_STYLE[d.dolibarr?.status] || STATUS_STYLE[0]}`} data-testid="devis-history-status">{d.dolibarr?.status_label}</span>
                </div>
                <p className="text-sm mt-1.5">{d.computed?.blocks?.map((b) => b.label).join(' · ') || '—'} · <span className="font-semibold">{eur(d.dolibarr?.total_ttc ?? d.computed?.totals?.ttc)} TTC</span></p>
                {d.email?.at && <p className="text-[11px] text-muted-foreground mt-0.5 flex items-center gap-1" data-testid="devis-history-sent"><Mail className="h-3 w-3" /> Envoyé à {d.email.to} le {fmtShortDate(d.email.at)} {fmtTime(d.email.at)}</p>}
                <div className="mt-2 flex flex-wrap gap-2">
                  <Button asChild size="sm" variant="outline" className="h-9">
                    <a href={quotePdfUrl(d.id)} target="_blank" rel="noreferrer" data-testid="devis-history-pdf"><FileText className="h-4 w-4 mr-1" /> PDF <ExternalLink className="h-3 w-3 ml-1" /></a>
                  </Button>
                  {onSend && Number(d.dolibarr?.status) <= 1 && (
                    <Button type="button" size="sm" className="h-9 bg-[hsl(var(--primary))] hover:bg-[hsl(var(--primary))]/90 text-white" onClick={() => onSend(d)} data-testid="devis-history-send"><Mail className="h-4 w-4 mr-1" /> {d.email?.at ? 'Renvoyer' : 'Envoyer'}</Button>
                  )}
                  {d.photos?.count > 0 && <Button type="button" size="sm" variant="outline" className="h-9" onClick={() => showPhotos(d)} data-testid="devis-history-photos"><Camera className="h-4 w-4 mr-1" /> {d.photos.count} photo{d.photos.count > 1 ? 's' : ''}</Button>}
                  <Button type="button" size="sm" variant="outline" className="h-9" onClick={() => onDuplicate(d.inputs)} data-testid="devis-history-duplicate"><Copy className="h-4 w-4 mr-1" /> Dupliquer</Button>
                </div>
              </Card>
            </li>
          ))}
        </ul>
      )}
      <Dialog open={!!photosFor} onOpenChange={(o) => { if (!o) setPhotosFor(null); }}>
        <DialogContent className="max-w-md w-[95vw] max-h-[92vh] overflow-y-auto" data-testid="devis-history-photos-dialog">
          <DialogHeader>
            <DialogTitle>Photos du terrain</DialogTitle>
            <DialogDescription>{photosFor?.quote?.client?.name} · {photosFor?.quote?.dolibarr?.ref}</DialogDescription>
          </DialogHeader>
          {photosFor && (photosFor.items.length === 0 ? <p className="text-sm text-muted-foreground">Aucune photo.</p> : (
            <div className="grid grid-cols-2 gap-2">
              {photosFor.items.map((p) => (
                <a key={p.id} href={photoUrl(p.id)} target="_blank" rel="noreferrer" className="block rounded-lg overflow-hidden border border-border">
                  <img src={photoUrl(p.id)} alt={p.caption || 'Photo du terrain'} className="w-full aspect-square object-cover" loading="lazy" />
                  {p.caption && <span className="block text-[11px] px-1.5 py-1 truncate">{p.caption}</span>}
                </a>
              ))}
            </div>
          ))}
        </DialogContent>
      </Dialog>
    </div>
  );
}
